import * as React from "react";
import { Link } from "react-router-dom";
import { LocationDescriptor } from "history";
import { IntersectionOptions } from "react-intersection-observer";
// helpers
import { parseForSummary } from "@vingle/wtf-sdk/helpers/postContentParser";

import ShavedText from "common/components/shavedText";
// components
import RoundImage from "common/components/roundImage";
import Icon, { IconWrapper } from "common/icons";
import PACKS, { IIconPack } from "common/icons/packs";
import { FormattedNumber } from "react-intl";
import ImageHolder from "common/components/imageHolder";
import {
  IUseTrackTicketOption,
  useTrackTicket,
} from "@vingle/wtf-sdk/helpers/trackTicket";
import { useSafeLayoutEffect } from "@vingle/wtf-sdk/helpers/hook";

const styles = require("./briefCard.scss");

interface IProps {
  title: string | null;
  content: string;
  authorName: string;
  link: LocationDescriptor;
  authorProfileImage?: Vingle.IImage;
  isLike?: boolean;
  likesCount?: number;
  commentsCount?: number;
  isClipped?: boolean;
  clipCount?: number;
  trackTicket?: IUseTrackTicketOption;
  intersection?: IntersectionOptions;
}

const LINK_PREVIEW = "link-preview";
const TITLE_LINE_HEIGHT = 19;
const BODY_LINE_HEIGHT = 18;
const MEDIA_RATIO = 12.5 / 16;

const getMediaDirection = (media: Vingle.CardContent.MediaData | undefined) => {
  if (media) {
    if (media.type === LINK_PREVIEW) {
      return "horizontal";
    } else {
      if (media.height / media.width > MEDIA_RATIO) {
        return "vertical";
      } else {
        return "horizontal";
      }
    }
  }
  return "none";
};

function BriefCardMedia({ media }: { media: Vingle.CardContent.MediaData }) {
  const mediaDirection = getMediaDirection(media);
  const isVertical = mediaDirection === "vertical";
  const fit = isVertical ? "crop-top" : "cover";
  const veil = isVertical ? <div className={styles.veil} /> : null;
  switch (media.type) {
    case LINK_PREVIEW: {
      return (
        <div className={styles.imageWithVeil}>
          {media.image && (
            <ImageHolder
              fit={fit}
              src={media.image}
              previewColor={media.previewColor}
              blurHash={media.blurHash}
              width="100%"
              height="100%"
            />
          )}
          {veil}
          <Icon
            icon="COMPACT_CARD_LINKPREVIEW_BADGE"
            className={styles.linkPreviewBadge}
          />
        </div>
      );
    }
    case "image": {
      return (
        <div className={styles.imageWithVeil}>
          <ImageHolder
            fit={fit}
            src={media.src}
            previewColor={media.previewColor}
            blurHash={media.blurHash}
            srcSet={media.srcset}
            width="100%"
            height={"100%"}
          />
          {veil}
        </div>
      );
    }
    case "embedded-video":
    case "video": {
      const isGIF =
        media.type === "video" && media.originalType === "image/gif";
      return (
        <div className={styles.imageWithVeil}>
          <ImageHolder
            fit={fit}
            src={media.poster}
            previewColor={media.previewColor}
            blurHash={media.blurHash}
            width="100%"
            height={"100%"}
          />
          {veil}
          <Icon
            icon={isGIF ? "COMPACT_CARD_GIF_BADGE" : "COMPACT_CARD_VIDEO_BADGE"}
            className={isGIF ? styles.gifBadge : styles.videoBadge}
          />
        </div>
      );
    }
  }
}

function useBriefCard(props: IProps) {
  const titleRef = React.useRef<HTMLDivElement>(null);
  const [titleShaveHeight, setTitleShaveHeight] = React.useState(0);
  const [bodyShavedHeight, setBodyShavedHeight] = React.useState(0);
  const {
    content,
    title,
    likesCount,
    clipCount: shareCount,
    commentsCount,
  } = props;
  const { texts, medias } = React.useMemo(() => parseForSummary(content), [
    content,
  ]);
  const bodyText = texts.join("\n").trim();
  const hasEngage = Boolean(likesCount || shareCount || commentsCount);
  const media = medias.find(
    mediaItem => !(mediaItem.type === LINK_PREVIEW && !mediaItem.image),
  );
  const mediaDirection = getMediaDirection(media);
  useSafeLayoutEffect(() => {
    const titleNode = titleRef.current;
    let titleShaveHeightValue = 0;
    let bodyShavedHeightValue = 0;
    let MAX_TEXT_LINE_COUNT = hasEngage ? 2 : 3;
    if (mediaDirection === "none") {
      MAX_TEXT_LINE_COUNT = hasEngage ? 7 : 8;
    }
    if (titleNode) {
      if (titleNode.scrollHeight >= TITLE_LINE_HEIGHT * MAX_TEXT_LINE_COUNT) {
        titleShaveHeightValue = TITLE_LINE_HEIGHT * MAX_TEXT_LINE_COUNT;
        bodyShavedHeightValue = 0;
      } else {
        titleShaveHeightValue = title ? titleNode.scrollHeight : 0;
        bodyShavedHeightValue =
          (MAX_TEXT_LINE_COUNT -
            Math.floor(titleNode.scrollHeight / TITLE_LINE_HEIGHT)) *
          BODY_LINE_HEIGHT;
      }
    } else {
      bodyShavedHeightValue = MAX_TEXT_LINE_COUNT * BODY_LINE_HEIGHT;
    }
    setTitleShaveHeight(titleShaveHeightValue);
    setBodyShavedHeight(bodyShavedHeightValue);
  }, [bodyText, title, hasEngage]);
  return {
    titleRef,
    titleShaveHeight,
    bodyShavedHeight,
    bodyText,
    media,
    mediaDirection,
    hasEngage,
  };
}

const BriefCard = (props: IProps) => {
  const {
    link,
    isLike,
    title,
    trackTicket,
    authorProfileImage,
    authorName,
    clipCount,
    isClipped,
    likesCount,
    commentsCount,
  } = props;
  const {
    media,
    mediaDirection,
    bodyText,
    titleRef,
    titleShaveHeight,
    bodyShavedHeight,
    hasEngage,
  } = useBriefCard(props);
  const isFilled = mediaDirection === "vertical";
  const { handleRead, impressionRef } = useTrackTicket(trackTicket);
  return (
    <Link
      to={link}
      innerRef={impressionRef}
      onClick={handleRead}
      className={`${styles.card} ${styles[mediaDirection]}`}
    >
      {media && <BriefCardMedia media={media} />}
      <div className={styles.contents}>
        {title && (
          <div className={styles.title} ref={titleRef}>
            {titleShaveHeight ? (
              <ShavedText value={title || ""} shaveHeight={titleShaveHeight} />
            ) : (
              <span style={{ wordBreak: "break-all" }}>{title || ""}</span>
            )}
          </div>
        )}
        {bodyText && (
          <div className={styles.body}>
            {bodyShavedHeight ? (
              <ShavedText value={bodyText} shaveHeight={bodyShavedHeight} />
            ) : null}
          </div>
        )}
        {hasEngage ? (
          <div className={styles.stats}>
            {likesCount ? (
              <EngageUnit
                count={likesCount}
                icon={
                  isLike
                    ? PACKS.ENGAGE.SMALL_LIKE_ACTIVE
                    : isFilled
                    ? PACKS.ENGAGE.SMALL_LIKE_WHITE_NORMAL
                    : PACKS.ENGAGE.SMALL_LIKE_GREY_NORMAL
                }
              />
            ) : null}
            {clipCount ? (
              <EngageUnit
                count={clipCount}
                icon={
                  isClipped
                    ? PACKS.ENGAGE.SMALL_CLIP_ACTIVE
                    : isFilled
                    ? PACKS.ENGAGE.SMALL_CLIP_WHITE_NORMAL
                    : PACKS.ENGAGE.SMALL_CLIP_GREY_NORMAL
                }
              />
            ) : null}
            {commentsCount ? (
              <EngageUnit
                count={commentsCount}
                icon={
                  isFilled
                    ? PACKS.ENGAGE.SMALL_COMMENT_WHITE_NORMAL
                    : PACKS.ENGAGE.SMALL_COMMENT_GREY_NORMAL
                }
              />
            ) : null}
          </div>
        ) : null}
        <div className={styles.userInfo}>
          {authorProfileImage ? (
            <RoundImage imageSrc={authorProfileImage} width={16} height={16} />
          ) : null}
          <span className={styles.username}>{authorName}</span>
        </div>
      </div>
    </Link>
  );
};

interface IEngageUnitProps {
  icon: IIconPack;
  count: number;
}

const EngageUnit = ({ icon, count }: IEngageUnitProps) => {
  if (count < 1) return null;

  return (
    <div className={styles.engageUnit}>
      <IconWrapper icon={icon} />
      <span>
        <FormattedNumber value={count} />
      </span>
    </div>
  );
};

export default React.memo(BriefCard);
